import { useMemo, useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import type { Project, ProjectHealthSummary, SiteState } from '../api';
import { useQuery } from '../hooks';
import { queries } from '../queries';
import { EmptyState, ErrorNotice, SkeletonPage } from '../components/ui';
import { Callout, FilterChips, Icon, Input, Page, PageHeader, Select } from '../components/primitives';
import { ResponsiveTable } from '../components/primitives/ResponsiveTable';
import type { Column } from '../components/primitives/ResponsiveTable';
import { LevelBadge } from '../components/project/ui';
import { relativeTime } from '../components/project/projectHealth';
import { naturalCompare } from '../components/project/operationalHealth';
import { InfrastructureSummary, OutcomeBadge, SiteBadge, Timestamp, attemptNote } from '../components/status/StatusPieces';
import { infrastructureOf, latestAttemptOf, siteOf } from '../components/status/statusModel';
import { bySiteUrgency } from '../components/portfolio/PortfolioStatus';

type SiteFilter = 'all' | SiteState | 'failed_update';
type SortKey = 'name' | 'urgency' | 'deployed';

const filterLabel: Record<SiteFilter, string> = {
  all: 'All',
  unavailable: 'Site unavailable',
  degraded: 'Site degraded',
  operational: 'Site operational',
  unknown: 'Site not known',
  failed_update: 'Latest deployment failed',
};

interface Row {
  project: Project;
  health: ProjectHealthSummary | null;
}

const siteStateOf = (row: Row): SiteState => siteOf(row.health?.primary).state;
const failedUpdate = (row: Row) => latestAttemptOf(row.health?.primary)?.outcome === 'failed';

function matchesFilter(row: Row, filter: SiteFilter): boolean {
  if (filter === 'all') return true;
  if (filter === 'failed_update') return failedUpdate(row);
  return siteStateOf(row) === filter;
}

export function ProjectsPage() {
    const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  // Set by the project's Settings after a deletion (CO-74).
  const deletedProject = (location.state as { deletedProject?: string } | null)?.deletedProject ?? null;
  const projects = useQuery(queries.projects());
  const health = useQuery(queries.organizationHealth());

  const [query, setQuery] = useState('');
  const requested = searchParams.get('site') as SiteFilter | null;
  const filter: SiteFilter = requested && requested in filterLabel ? requested : 'all';
  const sort = (searchParams.get('sort') as SortKey | null) ?? 'name';

  function setParam(key: string, value: string, fallback: string) {
    const next = new URLSearchParams(searchParams);
    if (value === fallback) next.delete(key);
    else next.set(key, value);
    setSearchParams(next, { replace: true });
  }

  const allProjects = useMemo(() => projects.data?.projects ?? [], [projects.data]);
  const total = allProjects.length;
  const rows: Row[] = useMemo(() => {
    const byId = new Map((health.data?.projects ?? []).map((summary) => [summary.project_id, summary]));
    return allProjects.map((project) => ({ project, health: byId.get(project.id) ?? null }));
  }, [allProjects, health.data]);

  const counts = useMemo(() => {
    const out: Record<SiteFilter, number> = { all: rows.length, unavailable: 0, degraded: 0, operational: 0, unknown: 0, failed_update: 0 };
    for (const row of rows) {
      out[siteStateOf(row)] += 1;
      if (failedUpdate(row)) out.failed_update += 1;
    }
    return out;
  }, [rows]);

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const filtered = rows.filter((row) => {
      if (!matchesFilter(row, filter)) return false;
      if (!needle) return true;
      return `${row.project.name} ${row.project.slug} ${row.project.repository_url || ''}`.toLowerCase().includes(needle);
    });
    const byName = (a: Row, b: Row) => naturalCompare(a.project.name, b.project.name);
    const deployedAt = (row: Row) => {
      const latest = row.health?.primary?.deployments.latest;
      return latest ? Date.parse(latest.completed_at ?? latest.created_at) : -Infinity;
    };
    return [...filtered].sort((a, b) => {
      if (sort === 'urgency' && a.health && b.health) return bySiteUrgency(a.health, b.health);
      if (sort === 'deployed') return deployedAt(b) - deployedAt(a) || byName(a, b);
      return byName(a, b);
    });
  }, [rows, query, filter, sort]);

  return (
    <Page>
      <PageHeader
        title="Projects"
        actions={
          <Link to="/app/projects/new" className="button button--primary">
            <Icon name="plus" />
            New project
          </Link>
        }
      />

      {deletedProject ? (
        <Callout tone="success" title={`${deletedProject} was deleted from Commerce Ops`} testId="project-deleted-notice">
          Nothing on its servers was changed. The audit log keeps its history and the locations of its backup files.
        </Callout>
      ) : null}

      {projects.loading ? <SkeletonPage rows={4} /> : null}
      <ErrorNotice error={projects.error} />

      {!projects.loading && total === 0 ? (
        <div data-testid="empty-projects-state">
          <EmptyState title="No projects" description="">
            <Link to="/app/projects/new" className="button button--primary">
              <Icon name="plus" />
              New project
            </Link>
          </EmptyState>
        </div>
      ) : null}

      {!projects.loading && total > 0 ? (
        <div className="project-list-view">
          <div className="list-toolbar">
            <Input
              type="search"
              className="list-toolbar__search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name, slug or repository"
              aria-label="Search projects"
            />
            <label className="list-toolbar__sort">
              <span className="type-meta">Sort</span>
              <Select value={sort} onChange={(e) => setParam('sort', e.target.value, 'name')} aria-label="Sort projects">
                <option value="name">Name</option>
                <option value="urgency">Most urgent first</option>
                <option value="deployed">Last deployed</option>
              </Select>
            </label>
            <span className="list-toolbar__count type-meta tabular-nums">
              {visible.length} of {total}
            </span>
          </div>
          <FilterChips
            label="Show projects by their site or latest deployment"
            value={filter}
            onChange={(value) => setParam('site', value, 'all')}
            options={(['all', 'unavailable', 'degraded', 'operational', 'unknown', 'failed_update'] as SiteFilter[])
              .filter((value) => value === 'all' || value === 'operational' || counts[value] > 0)
              .map((value) => ({ value, label: filterLabel[value], count: counts[value] }))}
          />
          {health.error ? (
            <Callout tone="warning" title="The projects' state could not be read">
              {health.error.message}
            </Callout>
          ) : null}

          {visible.length === 0 ? (
            <p className="quiet-empty" data-testid="no-project-matches">
              No projects match{' '}
              <button
                type="button"
                className="link-button"
                onClick={() => {
                  setQuery('');
                  setParam('site', 'all', 'all');
                }}
              >
                Show every project
              </button>
            </p>
          ) : (
            <ResponsiveTable
              rows={visible}
              columns={projectColumns(health.loading)}
              rowKey={(row) => row.project.id}
              caption="Projects"
              testId="projects-table"
              rowProps={(row) => ({ testId: `project-row-${row.project.slug}` })}
              rowLink={(row) => ({ to: `/app/projects/${row.project.id}/overview`, accessibleName: `Open ${row.project.name}` })}
            />
          )}
        </div>
      ) : null}
    </Page>
  );
}

/**
 * The columns of the Projects list (CO-150): the site and the latest
 * deployment are separate facts, so "Failed — previous release active"
 * next to "Operational" can never read as a broken store.
 */
function projectColumns(loading: boolean): Column<Row>[] {
  const unknown = <span className="muted">{loading ? 'Reading…' : 'Not known'}</span>;
  return [
    {
      id: 'project',
      header: 'Project',
      card: 'title',
      width: 16,
      cell: ({ project, health }) => (
        <span className="cell-stack">
          <Link to={`/app/projects/${project.id}/overview`} className="cell-stack__primary" data-prefetch={`/app/projects/${project.id}/overview`}>
            {project.name}
          </Link>
          <span className="cell-stack__secondary">
            {[project.platform === 'wordpress' ? 'WordPress' : project.architecture === 'headless' ? 'Magento, headless' : 'Magento', health?.primary?.environment_name].filter(Boolean).join(' · ')}
          </span>
        </span>
      ),
    },
    {
      id: 'site',
      header: 'Site',
      card: 'status',
      width: 'status',
      cell: ({ health }) => {
        if (!health) return unknown;
        const site = siteOf(health.primary);
        return (
          <span className="cell-stack">
            <SiteBadge state={site.state} stale={site.stale} />
            {site.state !== 'operational' ? <span className="cell-stack__secondary">{site.failing_checks?.length ? `Failing: ${site.failing_checks.slice(0, 2).join(', ')}` : site.headline}</span> : null}
          </span>
        );
      },
    },
    {
      id: 'deployment',
      header: 'Latest deployment',
      width: 'fill',
      cell: ({ health }) => {
        const attempt = latestAttemptOf(health?.primary);
        if (!attempt) return health?.primary ? <span className="muted">No deployments</span> : unknown;
        const short = attemptNote(attempt);
        return (
          <span className="cell-stack">
            <span className="cell-inline">
              <OutcomeBadge outcome={attempt.outcome} />
              {short ? <span className="cell-stack__secondary">{short}</span> : null}
            </span>
            <span className="cell-stack__secondary">
              {attempt.deployment.component_name ? `${attempt.deployment.component_name} · ` : ''}
              <code translate="no">{attempt.deployment.commit_sha.slice(0, 7)}</code> · <Timestamp iso={attempt.deployment.completed_at ?? attempt.deployment.created_at} />
            </span>
          </span>
        );
      },
    },
    {
      id: 'infrastructure',
      header: 'Infrastructure',
      width: 16,
      cell: ({ health }) => {
        if (!health?.primary) return unknown;
        return <InfrastructureSummary infra={infrastructureOf(health.primary)} />;
      },
    },
    {
      id: 'backups',
      header: 'Backups',
      priority: 'secondary',
      width: 16,
      cell: ({ project, health }) => {
        const primary = health?.primary;
        if (project.platform === 'wordpress') return <span className="cell-stack__secondary">Update checkpoints</span>;
        if (!primary) return unknown;
        return (
          <span className="cell-stack">
            <LevelBadge level={primary.backups.level}>{primary.backups.headline}</LevelBadge>
            {primary.backups.last_success_at ? <span className="cell-stack__secondary">Last backup · {relativeTime(primary.backups.last_success_at)}</span> : null}
          </span>
        );
      },
    },
    {
      id: 'incidents',
      header: 'Incidents',
      priority: 'secondary',
      width: 8,
      cell: ({ project, health }) => {
        const primary = health?.primary;
        if (!primary) return unknown;
        return primary.monitoring.open_incidents > 0 ? (
          <Link to={`/app/projects/${project.id}/monitoring`}>{primary.monitoring.open_incidents} open</Link>
        ) : (
          <span className="cell-stack__secondary">None</span>
        );
      },
    },
  ];
}
